import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import ReactDOMServer from 'react-dom/server';
import { Chessground } from 'chessground';
import { Api } from 'chessground/api';
import { Color, Key } from 'chessground/types';
import { DrawShape } from 'chessground/draw';
import { Chess } from 'chess.js';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faAnglesLeft,
  faAngleLeft,
  faAngleRight,
  faAnglesRight,
  faXmark,
  faHandBackFist,
} from '@fortawesome/free-solid-svg-icons';
import { toDests } from '../utils/ChessUtils';
import { stockfishEngine, EngineEval } from '../utils/StockfishEngine';
import { clientLog } from '../utils/clientLog';
import { whiteWinPct } from './winChance';
import { MoveType } from '../types/MoveType';
import { getIcon } from '../utils/ChessTypes';

// One position in the navigable line: its FEN and the move that led to it (for the last-move
// highlight).
export type LinePosition = { fen: string; lastMove?: [string, string] };

export type CorrectionOutcome = {
  correct: boolean;
  playedUci: string;
  playedSan: string;
  // Win chance the played move gave up vs. best (mover's side); null when ungraded (no engine).
  dropPct: number | null;
  // The played move on the app's move scale: from dropPct, or the server's classification when the
  // game move was replayed. Null when ungraded.
  moveType: MoveType | null;
  // The browser engine's best move here, as SAN. Null when ungraded.
  bestMoveSan: string | null;
  // Position after the played move and the engine's best line from it (UCI), for the clickable
  // "From here:" continuation. Absent when ungraded.
  afterFen?: string;
  bestLineUci?: string[];
};

// SAN of a UCI move from a position.
function uciToSan(fen: string, uci: string): string | null {
  try {
    const c = new Chess(fen);
    const m = c.move({ from: uci.slice(0, 2), to: uci.slice(2, 4), promotion: uci[4] || 'q' });
    return m?.san ?? null;
  } catch {
    return null;
  }
}

function classifyDrop(drop: number): MoveType {
  if (drop >= 20) return MoveType.Blunder;
  if (drop >= 10) return MoveType.Mistake;
  if (drop >= 5) return MoveType.Inaccuracy;
  if (drop >= 2) return MoveType.Good;
  return MoveType.Best;
}

// Chessground arrow brushes in the move-type label colours (site.css `.cm-cp-move-type.*`), so the
// "from here" arrow reads in the evaluation's colour.
const MOVE_TYPE_BRUSHES = {
  // DrawBrushes requires chessground's four defaults; custom keys extend them.
  green: { key: 'g', color: '#15781B', opacity: 1, lineWidth: 10 },
  red: { key: 'r', color: '#882020', opacity: 1, lineWidth: 10 },
  blue: { key: 'b', color: '#003088', opacity: 1, lineWidth: 10 },
  yellow: { key: 'y', color: '#e68f00', opacity: 1, lineWidth: 10 },
  mtBlunder: { key: 'mtBlunder', color: '#e5533c', opacity: 0.9, lineWidth: 11 },
  mtMistake: { key: 'mtMistake', color: '#f0883e', opacity: 0.9, lineWidth: 11 },
  mtMiss: { key: 'mtMiss', color: '#f28b82', opacity: 0.9, lineWidth: 11 },
  mtInaccuracy: { key: 'mtInaccuracy', color: '#e3b341', opacity: 0.9, lineWidth: 11 },
  mtGood: { key: 'mtGood', color: '#22c55e', opacity: 0.9, lineWidth: 11 },
  // The piece hint: a light blue ring, deliberately in no move-type colour since it grades nothing.
  hintPiece: { key: 'hintPiece', color: '#7cd3ff', opacity: 0.95, lineWidth: 12 },
};

function brushForMoveType(mt: MoveType): string {
  switch (mt) {
    case MoveType.Blunder:
      return 'mtBlunder';
    case MoveType.Mistake:
      return 'mtMistake';
    case MoveType.Miss:
      return 'mtMiss';
    case MoveType.Inaccuracy:
      return 'mtInaccuracy';
    default:
      return 'mtGood';
  }
}

// How long the board holds the position before the mistake before playing the move in.
const INTRO_HOLD_MS = 1000;

// Both fields, not just mateIn: mate 0 carries the winner in the score's sign (see EngineEval).
function whiteWinOf(e: EngineEval): number {
  return whiteWinPct(e.scoreCp, e.mateIn);
}

function moverWin(e: EngineEval, sideToMove: Color): number {
  const white = whiteWinOf(e);
  return sideToMove === 'white' ? white : 100 - white;
}

// Play a UCI line out from a FEN, collecting each position and its numbered SAN for the best-line
// chips. Capped to stay glanceable.
function buildForward(
  fen: string,
  uci: string[],
  max = 8,
): { positions: LinePosition[]; plies: { san: string; numberLabel: string | null }[] } {
  const positions: LinePosition[] = [];
  const plies: { san: string; numberLabel: string | null }[] = [];
  try {
    const c = new Chess(fen);
    for (let i = 0; i < Math.min(uci.length, max); i++) {
      const t = uci[i];
      if (t.length < 4) break;
      const number = c.moveNumber();
      const white = c.turn() === 'w';
      const mv = c.move({ from: t.slice(0, 2), to: t.slice(2, 4), promotion: t[4] || 'q' });
      if (mv == null) break;
      positions.push({ fen: c.fen(), lastMove: [mv.from, mv.to] });
      plies.push({
        san: mv.san,
        numberLabel: white ? `${number}.` : i === 0 ? `${number}…` : null,
      });
    }
  } catch {
    return { positions: [], plies: [] };
  }
  return { positions, plies };
}

// The interactive "correct your mistake" board. It opens right before the mistake and any
// good-or-better move counts, graded live by the in-browser engine on this exact position. The played
// move stays on the board. Step controls walk back through the game; after a move, the engine's best
// line from it is appended, clickable to play out, and its first move is drawn as an arrow coloured
// by how the move graded. Nothing is revealed before the player moves.
export default function CorrectionBoard(props: {
  history: LinePosition[]; // game start … solve position (inclusive)
  solveIndex: number; // index of the solve position within history (= history.length - 1)
  solution: string; // server's stored best move, UCI; only a fallback when there's no engine
  solveEvalCp: number | null; // stored eval of the solve position (white cp), seeds the eval bar
  sideToMove: Color;
  // The move played in the game here (SAN) and the server's classification. Replaying it keeps the
  // server's verdict: it's the flagged mistake, so it can never come back "good".
  gamePlayedSan: string;
  gameMoveType: MoveType;
  // The Hint reveal: an arrow for the game move, coloured by severity. Cleared once the player moves.
  showHint?: boolean;
  // The last hint rung: ring the square the engine's move starts from. Cleared once they answer.
  pieceHintSquare?: Key | null;
  // "Show me the answer": a green arrow for the move to find (UCI), before the player has answered.
  answerUci?: string | null;
  // Open one move short of the solve position and play that move in after a beat. Watching the last
  // move land tells you what the position is asking.
  playIntro?: boolean;
  onResult: (outcome: CorrectionOutcome) => void;
  // Optional node to portal the "From here" best-line row into (below the result buttons). Renders
  // inline under the controls when absent.
  bestLineSlot?: HTMLElement | null;
  // Optional access to the board's position for a moves list: `navRef` gets a jump function,
  // `onNavChange` fires whenever the board moves.
  navRef?: React.MutableRefObject<((index: number) => void) | null>;
  onNavChange?: (cursor: number) => void;
}): React.ReactNode {
  const {
    history,
    solveIndex,
    solution,
    solveEvalCp,
    sideToMove,
    gamePlayedSan,
    gameMoveType,
    showHint,
    pieceHintSquare,
    answerUci,
    playIntro,
    onResult,
    bestLineSlot,
    navRef,
    onNavChange,
  } = props;
  const boardRef = useRef<HTMLDivElement>(null);
  const apiRef = useRef<Api | null>(null);
  const resultCbRef = useRef(onResult);
  resultCbRef.current = onResult;
  const attemptRef = useRef(0);

  const solveFen = history[solveIndex].fen;
  const solveChess = useMemo(() => new Chess(solveFen), [solveFen]);
  // The game move, for the Hint arrow. Fixed until the player moves.
  const hintArrow = useMemo(() => {
    try {
      const c = new Chess(solveFen);
      const mv = c.move(gamePlayedSan);
      return mv ? { orig: mv.from as Key, dest: mv.to as Key } : null;
    } catch {
      return null;
    }
  }, [solveFen, gamePlayedSan]);
  const [checking, setChecking] = useState(false);
  // The played move and the best line from it, once a move has been made.
  const [result, setResult] = useState<{
    afterFen: string;
    lastMove: [string, string];
    bestLineUci: string[];
    // The played move's classification, once graded — drives the "from here" arrow's colour.
    moveType: MoveType | null;
  } | null>(null);
  // The intro: hold before the mistake, then play the move into it. Needs a previous position.
  const introFrom = playIntro && solveIndex > 0 ? solveIndex - 1 : null;
  const [cursor, setCursor] = useState(introFrom ?? solveIndex);
  useEffect(() => {
    if (introFrom == null) return;
    const timer = setTimeout(() => {
      // Unless the player already stepped somewhere during the pause.
      setCursor((c) => (c === introFrom ? solveIndex : c));
    }, INTRO_HOLD_MS);
    return () => clearTimeout(timer);
    // Mount only: the board is remounted per mistake and per attempt.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Free-explore mode, via the hand toggle (or a board tap) and automatically after grading:
  //  · exploreArmed: the hand is on, so a drag explores instead of answering.
  //  · exploreLine: the line branched off where you started dragging; replaces the default line.
  //  · exploreFeedback: the engine's best move here (green arrow) and the grade of the move that
  //    reached it (badge).
  const [exploreArmed, setExploreArmed] = useState(false);
  const [exploreLine, setExploreLine] = useState<LinePosition[] | null>(null);
  const [exploreFeedback, setExploreFeedback] = useState<{
    best: [Key, Key] | null;
    badge: { square: Key; moveType: MoveType } | null;
  } | null>(null);

  // Live eval bar: White's win chance on the board, from the browser engine and cached per FEN. Seeded
  // from the stored eval; the last value stays while a new one computes.
  const evalCacheRef = useRef<Map<string, number>>(new Map());
  // Full engine reads per FEN; explore feedback needs the best move, not just the percentage.
  const engineCacheRef = useRef<Map<string, EngineEval>>(new Map());
  const [whiteWin, setWhiteWin] = useState<number | null>(
    solveEvalCp != null ? whiteWinPct(solveEvalCp) : null,
  );

  // The played move sits right after the game history; the best line follows it.
  const playedIndex = history.length;
  const forward = useMemo(
    () =>
      result ? buildForward(result.afterFen, result.bestLineUci) : { positions: [], plies: [] },
    [result],
  );
  // The default line: the game up to the solve position, then the played move and best continuation.
  const defaultLine = useMemo<LinePosition[]>(() => {
    if (!result) return history;
    return [...history, { fen: result.afterFen, lastMove: result.lastMove }, ...forward.positions];
  }, [result, history, forward]);
  const navLine = exploreLine ?? defaultLine;

  // A drag answers only at the solve position, unsolved, with explore off and no exploration.
  const isSolveDrag = () =>
    result == null && !exploreArmed && exploreLine == null && cursor === solveIndex;

  // An explore drag: play from the board's position, branching a fresh line. The eval effect grades it.
  const branchExplore = (orig: Key, dest: Key) => {
    const from = Math.min(cursor, navLine.length - 1);
    const c = new Chess(navLine[from].fen);
    let mv;
    try {
      mv = c.move({ from: orig, to: dest, promotion: 'q' });
    } catch {
      return;
    }
    if (!mv) return;
    const line: LinePosition[] = [
      ...navLine.slice(0, from + 1),
      { fen: c.fen(), lastMove: [orig, dest] },
    ];
    setExploreLine(line);
    setCursor(line.length - 1);
  };

  // Leave explore, back to the correction (or your graded move).
  const exitExplore = () => {
    setExploreArmed(false);
    setExploreLine(null);
    setExploreFeedback(null);
    setCursor(result ? playedIndex : solveIndex);
  };

  const handleMove = async (orig: Key, dest: Key) => {
    // Anything that isn't the solve answer is exploration.
    if (!isSolveDrag()) {
      branchExplore(orig, dest);
      return;
    }
    const attempt = attemptRef.current;
    const probe = new Chess(solveFen);
    let played;
    try {
      played = probe.move({ from: orig, to: dest, promotion: 'q' });
    } catch {
      return;
    }
    const playedUci = `${orig}${dest}`;
    const afterFen = probe.fen();
    const lastMove: [string, string] = [orig, dest];

    // Leave the played move on the board and never revert it, even while grading.
    setResult({ afterFen, lastMove, bestLineUci: [], moveType: null });
    setCursor(playedIndex);

    // Replaying the exact game move keeps the server's verdict: it can never count as corrected.
    const isSameAsGame = played.san === gamePlayedSan;

    const report = (
      correct: boolean,
      dropPct: number | null,
      moveType: MoveType | null,
      bestMoveSan: string | null,
    ) => {
      resultCbRef.current({
        correct,
        playedUci,
        playedSan: played!.san,
        dropPct,
        moveType,
        bestMoveSan,
      });
    };

    // Checkmate is graded off the board: nothing beats mate, and the engine can't search a finished
    // position ("mate 0" has no side).
    if (probe.isCheckmate() && !isSameAsGame) {
      const white = sideToMove === 'white' ? 100 : 0;
      evalCacheRef.current.set(afterFen, white);
      setWhiteWin(white);
      setResult({ afterFen, lastMove, bestLineUci: [], moveType: MoveType.Best });
      report(true, 0, MoveType.Best, played.san);
      return;
    }

    // No engine in this browser: exact match against the server's best move only.
    if (!stockfishEngine.isSupported()) {
      const ok = solution.slice(0, 4) === playedUci;
      report(ok, null, isSameAsGame ? gameMoveType : null, uciToSan(solveFen, solution));
      return;
    }

    // Grade with the browser engine on this position: best move, line and drop all come from the client.
    setChecking(true);
    try {
      const [best, after] = await Promise.all([
        stockfishEngine.evaluate(solveFen),
        stockfishEngine.evaluate(afterFen),
      ]);
      if (attemptRef.current !== attempt) return;
      const drop = Math.round(moverWin(best, sideToMove) - moverWin(after, sideToMove));
      // The exact game move keeps the server's classification and stays uncorrected.
      const correct = isSameAsGame ? false : drop < 5;
      const dropPct = isSameAsGame ? null : drop;
      const moveType = isSameAsGame ? gameMoveType : classifyDrop(drop);
      // Seed the eval bar's cache from the grade.
      evalCacheRef.current.set(solveFen, whiteWinOf(best));
      evalCacheRef.current.set(afterFen, whiteWinOf(after));
      setWhiteWin(whiteWinOf(after));
      // The best line from the position after your move.
      setResult({ afterFen, lastMove, bestLineUci: after.pv, moveType });
      report(correct, dropPct, moveType, best.bestMove ? uciToSan(solveFen, best.bestMove) : null);
    } catch (err) {
      // Engine unavailable or timed out: exact match against the server's best, so the attempt still
      // resolves, and log it.
      if (attemptRef.current === attempt) {
        clientLog.warn('Correction grade fell back. Engine failed', {
          fen: solveFen,
          playedUci,
          message: err instanceof Error ? err.message : String(err),
        });
        const ok = solution.slice(0, 4) === playedUci;
        report(ok, null, isSameAsGame ? gameMoveType : null, uciToSan(solveFen, solution));
      }
    } finally {
      if (attemptRef.current === attempt) setChecking(false);
    }
  };
  const moveRef = useRef(handleMove);
  moveRef.current = handleMove;

  // Create the board once.
  useEffect(() => {
    if (!boardRef.current) return;
    const cg = Chessground(boardRef.current, {
      // Opens on the solve position, or one move short when playing the intro.
      fen: history[introFrom ?? solveIndex]?.fen ?? solveFen,
      lastMove: history[introFrom ?? solveIndex]?.lastMove as [Key, Key] | undefined,
      orientation: sideToMove,
      coordinates: true,
      animation: { enabled: true, duration: 200 },
      movable: {
        free: false,
        showDests: true,
        events: { after: (o, d) => void moveRef.current(o as Key, d as Key) },
      },
      drawable: { enabled: false, visible: true, brushes: MOVE_TYPE_BRUSHES },
    });
    apiRef.current = cg;
    // Chessground caches the board's rect and only rechecks on resize or scroll. A modal mounts it
    // mid-fade, so clicks land a square off; redraw after the next frame and when the transition ends.
    const settle = () => apiRef.current?.redrawAll();
    const frame = requestAnimationFrame(settle);
    const dialog = boardRef.current.closest('.modal');
    dialog?.addEventListener('transitionend', settle);
    return () => {
      cancelAnimationFrame(frame);
      dialog?.removeEventListener('transitionend', settle);
      // Invalidate any in-flight grade so it can't setState after unmount.
      attemptRef.current += 1;
      cg.destroy();
      apiRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync board to the current cursor position (and dragging availability).
  useEffect(() => {
    const cg = apiRef.current;
    if (!cg) return;
    const pos = navLine[Math.min(cursor, navLine.length - 1)] ?? { fen: solveFen };
    const turn: Color = pos.fen.split(' ')[1] === 'b' ? 'black' : 'white';
    // Two draggable modes: the solve (solver's side only) and free explore (either side, anywhere).
    const solveMode = isSolveDrag();
    const exploreMode =
      !solveMode &&
      (exploreArmed || exploreLine != null || (result != null && cursor >= playedIndex));
    const canDrag = !checking && (solveMode || exploreMode);
    const dragColor: Color | undefined = !canDrag ? undefined : solveMode ? sideToMove : turn;
    const dragDests = !canDrag
      ? new Map()
      : solveMode
        ? toDests(solveChess)
        : toDests(new Chess(pos.fen));
    cg.set({
      fen: pos.fen,
      orientation: sideToMove,
      turnColor: turn,
      lastMove: pos.lastMove as [Key, Key] | undefined,
      movable: { free: false, color: dragColor, dests: dragDests },
    });

    const autoShapes: DrawShape[] = [];
    const shapes: DrawShape[] = [];
    // At the just-played position, an arrow for the first best-line move, coloured by the grade.
    if (
      exploreLine == null &&
      !exploreArmed &&
      result?.moveType != null &&
      cursor === playedIndex &&
      forward.positions.length > 0
    ) {
      const arrow = forward.positions[0].lastMove;
      if (arrow) {
        autoShapes.push({
          orig: arrow[0] as Key,
          dest: arrow[1] as Key,
          brush: brushForMoveType(result.moveType),
        });
      }
    }
    // Hint: the game move before the player answers, coloured by its grade.
    if (result == null && showHint && hintArrow) {
      shapes.push({
        orig: hintArrow.orig,
        dest: hintArrow.dest,
        brush: brushForMoveType(gameMoveType),
      });
    }
    // Piece hint: a light blue ring on the square the engine's move starts from.
    if (result == null && pieceHintSquare != null) {
      shapes.push({ orig: pieceHintSquare, brush: 'hintPiece' });
    }
    if (result == null && answerUci != null && answerUci.length >= 4) {
      shapes.push({
        orig: answerUci.slice(0, 2) as Key,
        dest: answerUci.slice(2, 4) as Key,
        brush: 'green',
      });
    }
    // Explore feedback: green arrow for the best move, badge on the move that reached this position.
    if (exploreFeedback?.best) {
      shapes.push({ orig: exploreFeedback.best[0], dest: exploreFeedback.best[1], brush: 'green' });
    }
    if (exploreFeedback?.badge) {
      const Icon = getIcon(exploreFeedback.badge.moveType);
      const markup = ReactDOMServer.renderToStaticMarkup(
        <svg width="20" height="20" viewBox="0 0 8 8">
          <Icon />
        </svg>,
      );
      autoShapes.push({ orig: exploreFeedback.badge.square, customSvg: { html: markup } });
    }
    cg.setAutoShapes(autoShapes);
    cg.setShapes(shapes);
  }, [
    cursor,
    result,
    checking,
    navLine,
    forward,
    playedIndex,
    sideToMove,
    solveChess,
    solveFen,
    solveIndex,
    exploreArmed,
    exploreLine,
    exploreFeedback,
    showHint,
    hintArrow,
    pieceHintSquare,
    answerUci,
    gameMoveType,
  ]);

  // Keep the eval bar in step with the board, plus explore feedback while exploring.
  useEffect(() => {
    const idx = Math.min(cursor, navLine.length - 1);
    const pos = navLine[idx] ?? { fen: solveFen };
    const fen = pos.fen;
    // Feedback whenever exploring, so arming the hand shows the best move straight away.
    const wantFeedback = exploreArmed || exploreLine != null;

    const cached = evalCacheRef.current.get(fen);
    if (cached != null && !wantFeedback) {
      setWhiteWin(cached);
      return;
    }
    if (!stockfishEngine.isSupported()) return;
    let cancelled = false;
    const evalFull = (f: string): Promise<EngineEval> => {
      const hit = engineCacheRef.current.get(f);
      if (hit) return Promise.resolve(hit);
      return stockfishEngine.evaluate(f, { depth: 12 }).then((e) => {
        engineCacheRef.current.set(f, e);
        return e;
      });
    };

    evalFull(fen)
      .then(async (e) => {
        if (cancelled) return;
        const w = whiteWinOf(e);
        evalCacheRef.current.set(fen, w);
        setWhiteWin(w);
        if (!wantFeedback) {
          setExploreFeedback(null);
          return;
        }
        const best: [Key, Key] | null =
          e.bestMove && e.bestMove.length >= 4
            ? [e.bestMove.slice(0, 2) as Key, e.bestMove.slice(2, 4) as Key]
            : null;
        // Grade the move that reached this position (mover's side).
        let badge: { square: Key; moveType: MoveType } | null = null;
        if (idx > 0 && pos.lastMove) {
          const prev = navLine[idx - 1];
          const beforeEval = await evalFull(prev.fen);
          if (cancelled) return;
          const moverWhite = prev.fen.split(' ')[1] === 'w';
          const beforeMover = moverWhite ? whiteWinOf(beforeEval) : 100 - whiteWinOf(beforeEval);
          const afterMover = moverWhite ? w : 100 - w;
          badge = {
            square: pos.lastMove[1] as Key,
            moveType: classifyDrop(beforeMover - afterMover),
          };
        }
        if (!cancelled) setExploreFeedback({ best, badge });
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [cursor, navLine, solveFen, exploreArmed, exploreLine]);

  const last = navLine.length - 1;

  // Let a sibling (the repair session's moves list) drive and follow the board.
  if (navRef) {
    navRef.current = (index: number) => setCursor(Math.max(0, Math.min(navLine.length - 1, index)));
  }
  const navChangeRef = useRef(onNavChange);
  navChangeRef.current = onNavChange;
  useEffect(() => {
    navChangeRef.current?.(cursor);
  }, [cursor]);

  const viewerIsWhite = sideToMove === 'white';
  const evalLeft = whiteWin == null ? null : viewerIsWhite ? whiteWin : 100 - whiteWin;

  // The engine's best line from your move; click a move to play it out. Exit returns to your move.
  // Rendered into `bestLineSlot` when provided, otherwise inline.
  const bestLineNode =
    result && (forward.plies.length > 0 || cursor !== playedIndex || exploreLine != null) ? (
      <div className="cm-correct-bestline cm-cp-bestline">
        {forward.plies.length > 0 ? (
          <>
            <span className="cm-cp-bestline-label">From here:</span>
            {forward.plies.map((ply, i) => {
              const target = playedIndex + 1 + i;
              return (
                <React.Fragment key={i}>
                  {ply.numberLabel ? (
                    <span className="cm-cp-bestline-num cm-mono">{ply.numberLabel}</span>
                  ) : null}
                  <button
                    type="button"
                    className={`cm-cp-bestline-move cm-mono${
                      exploreLine == null && cursor === target ? ' active' : ''
                    }`}
                    onClick={() => {
                      setExploreLine(null);
                      setExploreFeedback(null);
                      setCursor(target);
                    }}
                    title="Play this line on the board"
                  >
                    {ply.san}
                  </button>
                </React.Fragment>
              );
            })}
          </>
        ) : null}
        {cursor !== playedIndex || exploreLine != null ? (
          <button
            type="button"
            className="cm-correct-exit"
            onClick={() => {
              setExploreLine(null);
              setExploreFeedback(null);
              setCursor(playedIndex);
            }}
            title="Exit, back to your move"
          >
            <FontAwesomeIcon icon={faXmark} className="me-1" />
            Exit
          </button>
        ) : null}
      </div>
    ) : null;

  return (
    <div className="cm-correct-board-wrap">
      {/* Eval bar on top of the board, following its orientation. */}
      {evalLeft != null ? (
        <div
          className={`cm-eval-bar cm-correct-eval-bar${viewerIsWhite ? '' : ' viewer-black'}`}
          role="progressbar"
          aria-valuenow={evalLeft}
          aria-valuemin={0}
          aria-valuemax={100}
          title={`White ${whiteWin}% · Black ${100 - (whiteWin ?? 0)}%`}
        >
          <div className="cm-eval-left" style={{ width: `${evalLeft}%` }}>
            <span className="cm-eval-num">{evalLeft}%</span>
          </div>
          <span className="cm-eval-num cm-eval-right-num">{100 - evalLeft}%</span>
        </div>
      ) : null}
      <div
        className="cm-correct-board"
        ref={boardRef}
        onClick={() => {
          // A tap jumps into explore, except at the correction, where it starts your answer.
          if (isSolveDrag() || exploreArmed || exploreLine != null) return;
          setExploreArmed(true);
          stockfishEngine.preload();
        }}
      />
      {checking ? <div className="cm-correct-checking">Checking your move…</div> : null}
      <div className="cm-correct-controls">
        <button
          type="button"
          className={`cm-correct-ctrl${exploreArmed || exploreLine != null ? ' is-active' : ''}`}
          onClick={() => {
            if (exploreArmed || exploreLine != null) {
              exitExplore();
            } else {
              setExploreArmed(true);
              stockfishEngine.preload();
            }
          }}
          title={
            exploreArmed || exploreLine != null
              ? 'Exit explore. Back to the correction'
              : 'Explore: move pieces freely; the engine shows the best move and grades yours'
          }
          aria-label="Explore mode"
        >
          <FontAwesomeIcon icon={exploreArmed || exploreLine != null ? faXmark : faHandBackFist} />
        </button>
        <button
          type="button"
          className="cm-correct-ctrl"
          onClick={() => setCursor(0)}
          disabled={cursor <= 0}
          title="Back to the start of the game"
          aria-label="First move"
        >
          <FontAwesomeIcon icon={faAnglesLeft} />
        </button>
        <button
          type="button"
          className="cm-correct-ctrl"
          onClick={() => setCursor((c) => Math.max(0, c - 1))}
          disabled={cursor <= 0}
          title="Previous move"
          aria-label="Previous move"
        >
          <FontAwesomeIcon icon={faAngleLeft} />
        </button>
        <button
          type="button"
          className="cm-correct-ctrl"
          onClick={() => setCursor((c) => Math.min(last, c + 1))}
          disabled={cursor >= last}
          title="Next move"
          aria-label="Next move"
        >
          <FontAwesomeIcon icon={faAngleRight} />
        </button>
        <button
          type="button"
          className="cm-correct-ctrl"
          onClick={() => setCursor(last)}
          disabled={cursor >= last}
          title="End of the line"
          aria-label="Last move"
        >
          <FontAwesomeIcon icon={faAnglesRight} />
        </button>
      </div>

      {bestLineSlot ? null : bestLineNode}
      {bestLineSlot && bestLineNode ? createPortal(bestLineNode, bestLineSlot) : null}
    </div>
  );
}
